import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { getEventPricing } from '../../lib/eventPricing'
import './Calendar.css'

const API_BASE_URL = ''

const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']

// Etkinlik türüne göre renk
const EVENT_TYPE_COLORS = {
  "Animasyon Gösterimi": "#F55C7A",
  "Anma Toplantısı": "#50C878",
  "Atölye Çalışmaları": "#F06292",
  "Bilim Merkezi Etkinlikleri": "#70D6FF",
  "Çocuk Kütüphanesi Etkinlikleri": "#FFD54F",
  "Çocuk Programı": "#FFB74D",
  "Doğa Yürüyüşü": "#8BC34A",
  "Eğitim": "#6A5ACD",
  "Festival Fuar": "#BA68C8",
  "Fotoğraf": "#9E9E9E",
  "Gençlik Kampı": "#84DCC6",
  "Gezi": "#00BFFF",
  "SAÜ Kampüs": "#A1887F",
  "Kano Etkinliği": "#4DB6AC",
  "Konferans": "#7986CB",
  "Konser": "#E574BC",
  "Mesleki Eğitim": "#FF8A65",
  "Okçuluk Turnuvası": "#D32F2F",
  "Özel Ders": "#FBC02D",
  "Özel Etkinlik": "#74BEC1",
  "Panel": "#64B5F6",
  "Satranç": "#5D4037",
  "Seminer": "#4DB6AC",
  "Sergi": "#FFD54F",
  "Sinema": "#FFB6C1",
  "Şiir Dinletisi": "#FF7043",
  "Sohbet": "#F68C70",
  "Sokak Basketbol Turnuvası": "#FF7043",
  "Sokak Voleybol Turnuvası": "#FF8A65",
  "Gençler Futbol Turnuvası": "#43A047",
  "Şölen": "#FFCA28",
  "Söyleşi": "#64B5F6",
  "Spor Etkinliği": "#79cb87",
  "Stand-up": "#9D8DF1",
  "Tiyatro": "#9D8DF1",
  "Turnuva Yarışları": "#FF8A65",
  "Yarışma": "#FF8A65",
  'default': '#94a3b8'
}

const getEventColor = (type) => EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.default

// Hava Durumu Yardımcıları
const getWeatherIcon = (code) => {
  if (code === 0) return "☀️";
  if (code >= 1 && code <= 3) return "⛅";
  if (code >= 45 && code <= 48) return "🌫️";
  if (code >= 51 && code <= 67) return "🌧️";
  if (code >= 71 && code <= 77) return "❄️";
  if (code >= 80 && code <= 82) return "🌦️";
  if (code >= 95 && code <= 99) return "⛈️";
  return "🌡️";
};

const getWeatherDesc = (code) => {
   if (code === 0) return "Açık";
   if (code >= 1 && code <= 3) return "Parçalı Bulutlu";
   if (code >= 45 && code <= 48) return "Sisli";
   if (code >= 51 && code <= 67) return "Yağmurlu";
   if (code >= 71 && code <= 77) return "Karlı";
   if (code >= 80 && code <= 82) return "Sağanak";
   if (code >= 95 && code <= 99) return "Fırtına";
   return "Hava Durumu";
};

// Tarih ve saat ayarlama (Türkiye +3)
const parseEventDateTime = (dateStr, timeStr) => {
  let date = null;
  let time = null;
  
  if (dateStr) {
    try {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        const localTimeMs = d.getTime() + (3 * 60 * 60 * 1000);
        const localD = new Date(localTimeMs);
        
        date = localD.toISOString().split('T')[0];
        if (dateStr.includes('T')) {
          time = localD.toISOString().split('T')[1].slice(0, 5);
        }
      }
    } catch(e) {}
  }
  
  if (!date && typeof dateStr === 'string') {
    date = dateStr.split('T')[0];
  }
  
  const finalTime = typeof timeStr === 'string' && timeStr.trim() !== '' ? timeStr.slice(0, 5) : (time || '10:00');
  
  return { date, time: finalTime };
};

// Rich text helper
function extractTextFromDescription(description) {
  if (!description) return ""
  if (typeof description === "string") return description
  if (Array.isArray(description)) {
    return description.map(block => {
      if (block.children && Array.isArray(block.children)) {
        return block.children.map(child => child.text || "").join("")
      }
      return ""
    }).join("\n")
  }
  return ""
}

const CalendarSection = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [filters, setFilters] = useState({ type: 'Tümü', location: 'Tümü' })
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [ setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedDateWeather, setSelectedDateWeather] = useState(null)
  const panelRef = useRef(null)

  // Hava durumu kontrolü
  useEffect(() => {
    if (selectedDate) {
      const today = new Date();
      const sDate = new Date(selectedDate);
      const diffDays = Math.ceil((sDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays >= -1 && diffDays <= 14) {
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=40.7583&longitude=30.3800&daily=weather_code,temperature_2m_max,temperature_2m_min&start_date=${selectedDate}&end_date=${selectedDate}&timezone=auto`)
          .then(res => res.json())
          .then(data => {
            if (data.daily && data.daily.weather_code && data.daily.weather_code.length > 0) {
              setSelectedDateWeather({
                code: data.daily.weather_code[0],
                max: data.daily.temperature_2m_max[0],
                min: data.daily.temperature_2m_min[0]
              });
            } else {
              setSelectedDateWeather(null);
            }
          })
          .catch(err => {
            console.error('Hava durumu alınamadı', err);
            setSelectedDateWeather(null);
          });
      } else {
        setSelectedDateWeather(null);
      }
    } else {
      setSelectedDateWeather(null);
    }
  }, [selectedDate]);

  // Responsive kontrolü
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Body scroll lock when modal is open
  useEffect(() => {
    if (selectedDate) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [selectedDate])

  // Swiper'ın panel üzerindeki kaydırma olaylarını yakalamasını engelle ve Mobil Drag-to-Close
  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    let startY = 0
    let currentY = 0
    let isDragging = false

    const stopPropagation = (e) => e.stopPropagation()

    const handleTouchStart = (e) => {
      e.stopPropagation()
      // Sadece mobilde ve panelin en tepesindeyken sürüklemeye izin ver
      if (isMobile && panel.scrollTop <= 0) {
        startY = e.touches[0].clientY
        isDragging = true
        panel.style.transition = 'none'
      }
    }

    const handleTouchMove = (e) => {
      e.stopPropagation()
      if (!isDragging) return

      const touchY = e.touches[0].clientY
      const diff = touchY - startY

      // Sadece aşağı doğru sürüklemeye izin ver
      if (diff > 0) {
        if (e.cancelable) e.preventDefault() // Sayfa kaydırmayı engelle
        currentY = diff
        // Hafif direnç efekti
        panel.style.transform = `translateY(${diff}px)`
      }
    }

    const handleTouchEnd = (e) => {
      e.stopPropagation()
      if (!isDragging) return

      isDragging = false
      panel.style.transition = 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)'

      // Eğer 100px'den fazla aşağı çekildiyse kapat
      if (currentY > 100) {
        panel.style.transform = 'translateY(100%)'
        setTimeout(() => {
          setSelectedDate(null)
          setSelectedEvent(null)
        }, 300)
      } else {
        // Geri yerine oturt
        panel.style.transform = ''
      }
      currentY = 0
    }

    // Native event listeners
    panel.addEventListener('wheel', stopPropagation, { passive: false })
    panel.addEventListener('touchstart', handleTouchStart, { passive: false })
    panel.addEventListener('touchmove', handleTouchMove, { passive: false })
    panel.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      panel.removeEventListener('wheel', stopPropagation)
      panel.removeEventListener('touchstart', handleTouchStart)
      panel.removeEventListener('touchmove', handleTouchMove)
      panel.removeEventListener('touchend', handleTouchEnd)
    }
  }, [selectedDate, isMobile])

  // Ay değiştiğinde etkinlikleri çek
  const fetchMonthEvents = useCallback(async (year, month) => {
    setLoading(true)
    try {
      const startDate = new Date(year, month, 1)
      const endDate = new Date(year, month + 1, 1)
      
      // ISO string formatına çevir (YYYY-MM-DD)
      // Timezone offset sorununu çözmek için yerel tarihi kullanıyoruz
      const startStr = new Date(startDate.getTime() - (startDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0]
      const endStr = new Date(endDate.getTime() - (endDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0]
      
      const url = `${API_BASE_URL}/api/events?populate=*&filters[create][$gte]=${startStr}&filters[create][$lt]=${endStr}&pagination[pageSize]=100`
      const response = await axios.get(url)
      
      const formattedEvents = response.data.data.map(event => {
        let imageUrl = null
        if (event.image) {
          if (event.image.url) {
            imageUrl = event.image.url.startsWith('http') ? event.image.url : `${API_BASE_URL}${event.image.url}`
          } else if (event.image.data?.attributes?.url) {
            const url = event.image.data.attributes.url
            imageUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`
          }
        }

        const eventDate = event.create || event.createDate || event.createdAt
        const { date: finalDate, time: finalTime } = parseEventDateTime(eventDate, event.time);
        
        const locationName = typeof event.location === 'object' && event.location !== null ? event.location.name : event.location;
        
        return {
          id: event.id,
          documentId: event.documentId,
          slug: event.slug,
          title: event.name || event.title || 'İsimsiz Etkinlik',
          date: finalDate,
          time: finalTime,
          endTime: typeof event.endTime === 'string' ? event.endTime.slice(0, 5) : '18:00',
          type: event.events || event.eventType || 'Etkinlik',
          location: locationName || 'Belirtilmemiş',
          description: extractTextFromDescription(event.description),
          cover: imageUrl,
          color: getEventColor(event.events || event.eventType),
          price: event.price,
          // Ücret/bilet durumu price_type + ticket + price üçlüsünden türetilir
          // (yalnızca price=0'a bakmak ücretli etkinlikleri "Ücretsiz" gösteriyordu)
          pricing: getEventPricing(event),
          quota: event.quota,
        }
      })

      setEvents(formattedEvents)
    } catch (err) {
      console.error('Etkinlikler yüklenemedi:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMonthEvents(currentDate.getFullYear(), currentDate.getMonth())
  }, [currentDate, fetchMonthEvents])

  // Ay değiştiğinde filtreleri sıfırla
  useEffect(() => {
    setFilters({ type: 'Tümü', location: 'Tümü' })
  }, [currentDate])

  // Bu aya ait dinamik filtre seçenekleri
  const availableEventTypes = useMemo(() => {
    const types = new Set(events.map(e => e.type).filter(Boolean))
    return ['Tümü', ...Array.from(types).sort()]
  }, [events])

  const availableLocations = useMemo(() => {
    const locations = new Set(events.map(e => e.location).filter(Boolean))
    return ['Tümü', ...Array.from(locations).sort()]
  }, [events])

  // Filtrelenmiş etkinlikler
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const typeMatch = filters.type === 'Tümü' || event.type === filters.type
      const locationMatch = filters.location === 'Tümü' || event.location === filters.location
      return typeMatch && locationMatch
    })
  }, [events, filters])

  // Takvim günlerini hesapla
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    
    let startDay = firstDay.getDay() - 1
    if (startDay < 0) startDay = 6
    
    const days = []
    const prevMonthLastDay = new Date(year, month, 0).getDate()
    
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, currentMonth: false, date: null })
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`
      days.push({ day: i, currentMonth: true, date: dateStr })
    }
    
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, currentMonth: false, date: null })
    }
    
    return days
  }, [currentDate])

  const getEventsForDate = (dateStr) => filteredEvents.filter(event => event.date === dateStr)

  const changeMonth = (delta) => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
    setSelectedDate(null)
    setSelectedEvent(null)
  }

  const goToToday = () => {
    setCurrentDate(new Date())
    setSelectedDate(null)
    setSelectedEvent(null)
  }

  const handleDateClick = (day) => {
    if (day.currentMonth && day.date) {
      setSelectedDate(day.date)
      setSelectedEvent(null)
    }
  }

  const generateCalendarLinks = (event) => {
    const dateStr = event.date.replace(/-/g, '')
    const startTimeStr = event.time.replace(/:/g, '')
    const endTimeStr = event.endTime.replace(/:/g, '')

    const startDateTime = `${dateStr}T${startTimeStr}00`
    const endDateTime = `${dateStr}T${endTimeStr}00`

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${startDateTime}/${endDateTime}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`
    
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.title)}&startdt=${event.date}T${event.time}:00&enddt=${event.date}T${event.endTime}:00&body=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startDateTime}
DTEND:${endDateTime}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`
    const icsUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`

    return { googleUrl, outlookUrl, icsUrl }
  }

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : []

  return (
    <div className={`calendar-page ${isMobile ? "" : "swiper-no-swiping"}`}>
      {/* Unified Calendar Container */}
      <div className="calendar-unified-container">
        {/* Header */}
        <header className="calendar-page-header">
          <h1 className="calendar-page-title">Etkinlik Takvimi</h1>
          <p className="calendar-page-subtitle">Kültür ve sanat etkinliklerimizi keşfedin</p>
        </header>

        {/* Toolbar: Filters + Navigation + Legend */}
        <div className="calendar-toolbar">
          <div className="calendar-toolbar-left">
            {/* Mobil Filtre Butonu */}
            <button 
              className="mobile-filter-btn"
              onClick={() => setShowFilters(true)}
            >
              <span className="filter-icon">🌪️</span> Filtrele
            </button>

            {/* Filtre Alanı - Mobilde Modal/Drawer Olur */}
            <div className={`filters-container ${showFilters ? 'mobile-active' : ''}`}>
              <div className="mobile-filters-header">
                <h3>Filtrele</h3>
                <button className="mobile-close-btn" onClick={() => setShowFilters(false)}>✕</button>
              </div>

              <div className="filter-item">
                <label>Tür:</label>
                <select
                  aria-label="Etkinlik türüne göre filtrele"
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))
                  }
                >
                  {availableEventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <label>Mekan:</label>
                <select
                  aria-label="Mekana göre filtrele"
                  value={filters.location}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))
                  }
                >
                  {availableLocations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              {(filters.type !== 'Tümü' || filters.location !== 'Tümü') && (
                <button 
                  className="filter-reset-btn"
                  onClick={() => setFilters({ type: 'Tümü', location: 'Tümü' })}
                >
                  ✕ Temizle
                </button>
              )}
              
              <button className="mobile-filter-apply" onClick={() => setShowFilters(false)}>
                Uygula
              </button>
            </div>
            
            {/* Mobil Overlay */}
            {showFilters && <div className="mobile-filters-overlay" onClick={() => setShowFilters(false)} />}
          </div>

          <div className="calendar-toolbar-center">
            <button className="nav-btn" onClick={() => changeMonth(-1)}>‹</button>
            <h2 className="month-title">{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
            <button className="nav-btn" onClick={() => changeMonth(1)}>›</button>
            <button className="today-btn" onClick={goToToday}>Bugün</button>
          </div>

          <div className="calendar-toolbar-right">
            <div className="calendar-stats-legend">
              <span className="stat-badge">{filteredEvents.length} Etkinlik</span>
              
              <div className="legend-compact">
                {availableEventTypes.filter(t => t !== 'Tümü').map(type => (
                  <div key={type} className="legend-compact-item">
                    <span className="legend-compact-color" style={{ backgroundColor: getEventColor(type) }}></span>
                    <span className="legend-compact-text">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Calendar */}
        <div className="calendar-main-container">
          {loading && (
            <div className="calendar-loading">
              <div className="spinner"></div>
              <span>Etkinlikler yükleniyor...</span>
            </div>
          )}

          <div className="calendar-grid-container">
            {/* Day Headers */}
            <div className="calendar-day-headers">
              {DAYS.map(day => (
                <div key={day} className="day-header">{day}</div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="calendar-days-grid">
              {calendarDays.map((day, index) => {
                const dayEvents = day.date ? getEventsForDate(day.date) : []
                const isToday = day.date === new Date().toISOString().split('T')[0]
                const isSelected = day.date === selectedDate
                const hasEvents = dayEvents.length > 0

                return (
                  <div 
                    key={index}
                    className={`calendar-day-cell ${!day.currentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasEvents ? 'has-events' : ''}`}
                    onClick={() => handleDateClick(day)}
                  >
                    <span className={`day-number ${isToday ? 'today-number' : ''}`}>
                      {day.day}
                    </span>

                    {hasEvents && (
                      <div className="day-events-preview">
                        {/* Show cover previews for up to 3 events */}
                        {!isMobile && dayEvents.slice(0, 3).map((event, i) => (
                          event.cover && (
                            <div 
                              key={i}
                              className="event-cover-preview"
                              style={{ backgroundImage: `url(${event.cover})` }}
                              title={event.title}
                            />
                          )
                        ))}
                        
                        {/* Show pills if no covers or on mobile */}
                        {(isMobile || !dayEvents[0]?.cover) && (
                          <div className="event-pills">
                            {dayEvents.slice(0, isMobile ? 5 : 3).map((event, i) => (
                              <div 
                                key={i}
                                className="event-pill"
                                style={{ 
                                  backgroundColor: isMobile ? event.color : `${event.color}20`,
                                  borderLeftColor: event.color 
                                }}
                                title={event.title}
                              >
                                {!isMobile && (
                                  <span className="event-pill-text">
                                    {event.title.substring(0, 15)}
                                    {event.title.length > 15 ? '...' : ''}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* More events badge */}
                        {dayEvents.length > 3 && (
                          <span className="more-events-badge">+{dayEvents.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Event Detail Modal - Outside unified container */}
      {selectedDate && createPortal(
        <>
          <div className="modal-overlay" onClick={() => { setSelectedDate(null); setSelectedEvent(null); }} />
          <aside ref={panelRef} className={`event-detail-panel ${isMobile ? 'mobile' : ''} swiper-no-swiping`}>
            <div className="panel-header">
              <h2 className="panel-title">
                📅 {new Date(selectedDate).toLocaleDateString('tr-TR', { 
                  day: 'numeric', month: 'long', year: 'numeric', weekday: 'long'
                })}
              </h2>
              {selectedDateWeather && (
                <div className="panel-weather" title={getWeatherDesc(selectedDateWeather.code)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#64748b', background: 'rgba(0,0,0,0.03)', padding: '0.3rem 0.6rem', borderRadius: '1rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>{getWeatherIcon(selectedDateWeather.code)}</span>
                  <span>{Math.round(selectedDateWeather.min)}° - {Math.round(selectedDateWeather.max)}°</span>
                </div>
              )}
              <button 
                className="panel-close-btn"
                onClick={() => { setSelectedDate(null); setSelectedEvent(null); }}
              >✕</button>
            </div>

            {selectedDateEvents.length === 0 ? (
              <div className="no-events">
                <span className="no-events-icon">📭</span>
                <p>Bu tarihte etkinlik bulunmuyor.</p>
              </div>
            ) : (
              <div className="events-list">
                {selectedDateEvents.map(event => (
                  <div 
                    key={event.id}
                    className={`event-card ${selectedEvent?.id === event.id ? 'expanded' : ''}`}
                    style={{ borderLeftColor: event.color }}
                    onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                  >
                    {event.cover && (
                      <div 
                        className="event-card-cover"
                        style={{ backgroundImage: `url(${event.cover})` }}
                      />
                    )}
                    <div className="event-card-body">
                      <h3 className="event-card-title">{event.title}</h3>
                      <div className="event-card-meta">
                        <span>🕐 {event.time}</span>
                        <span>📍 {event.location}</span>
                      </div>
                      <div className="event-card-badges">
                        <span
                          className="event-type-badge"
                          style={{ backgroundColor: event.color }}
                        >
                          {event.type}
                        </span>
                        {event.pricing.label && (
                          <span className={`event-price-badge ${event.pricing.isPaid ? 'event-price-paid' : 'event-price-free'}`}>
                            {event.pricing.isPaid ? `💳 ${event.pricing.label}` : `✓ ${event.pricing.label}`}
                          </span>
                        )}
                        {event.pricing.isTicketed && (
                          <span className="event-price-badge event-ticket-badge">🎟 Biletli</span>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {selectedEvent?.id === event.id && (
                      <div className="event-expanded-details">
                        {event.description && (
                          <p className="event-description">{event.description}</p>
                        )}
                        {(event.pricing.label || event.pricing.ticketLabel) && (
                          <p className="event-price">
                            💰 {[event.pricing.label, event.pricing.ticketLabel].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        
                        <div className="calendar-add-section">
                          <p className="section-label">Takvime Ekle:</p>
                          <div className="calendar-buttons">
                            <a 
                              href={generateCalendarLinks(event).googleUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="calendar-add-btn"
                            >📅 Google</a>
                            <a 
                              href={generateCalendarLinks(event).outlookUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="calendar-add-btn"
                            >📧 Outlook</a>
                            <a 
                              href={generateCalendarLinks(event).icsUrl}
                              download={`${event.title}.ics`}
                              className="calendar-add-btn"
                            >🍎 Apple</a>
                          </div>
                        </div>

                        <Link 
                          to={`/etkinlik/${event.slug || event.documentId || event.id}`}
                          className="event-detail-link"
                        >
                          Detayları Gör →
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </aside>
        </>
      , document.body)}
    </div>
  )
}

export default CalendarSection
